import styles from "./style.module.scss";
import { h } from "preact";
import { useRef, useState } from "preact/hooks";
import { Text } from "preact-i18n";
import type MvInputParams from "models/MvInputParams";
import type MvArea from "models/MvArea";
import type { CountryCode } from "models/CountryCode";
import type DefaultOptions from "models/DefaultOptions";
import type MvAdressResult from "models/MvAddressResult";
import type { LoadingActions } from "components/StoreContextWrapper/actions";
import { getAddressCount, useClickInside } from "services/helpers";
import Button from "components/Button/Button";
import AreaFetchResult from "components/AreaFetchResult/AreaFetchResult";
import AreaInputForm from "components/AreaInputForm/AreaInputForm";
import AreaHeader from "./AreaHeader";
import AreaFilters from "./AreaFilters";
import { useAreaRequests } from "./useAreaRequests";

export interface AreaProps extends Partial<LoadingActions> {
    addressData: MvArea;
    isDisabled?: boolean;
    apiBasePath?: string;
    isSelected?: boolean;
    onRemove?(area: MvArea): void;
    onChange(id: string, address: MvAdressResult | null): void;
    onSelect(id: string): void;
    onUpdateParams(id: string, patch: Partial<MvInputParams>): void;
    onAddressesFound(id: string, addresses: MvAdressResult[]): void;
    onShowAddresses(): void;
    pricePerAddress: number;
    countryCode?: CountryCode;
    defaultOptions: DefaultOptions;
}
const Area = (props: AreaProps) => {
    const {
        addressData,
        isDisabled = false,
        isSelected: selected = false,
        onRemove,
        onChange,
        onSelect,
        onUpdateParams,
        onShowAddresses,
        pricePerAddress,
        countryCode,
        defaultOptions,
    } = props;
    const currentAddress = addressData.result ?? null;
    const [isOpen, setIsOpen] = useState(true);
    const {
        postalCode,
        streetName,
        houseNumber,
        setPostalCode,
        setStreetName,
        setHouseNumber,
        isFetching,
        error,
        fetchResults,
        setFetchResults,
        showForm,
        setShowForm,
        cancelRequests,
    } = useAreaRequests(props);

    const selectedAddressCount = getAddressCount(addressData.addresses);
    const clickRef = useRef<HTMLDivElement>(null);
    useClickInside(clickRef, () => {
        if (!selected) onSelect(addressData.id);
    });
    const handleRemove = () => {
        cancelRequests();
        onRemove?.(addressData);
    };
    return (
        <div
            className={`${styles.mvWrapper} ${
                isDisabled ? styles.disabled : ""
            } ${isOpen ? styles.open : ""} ${selected ? styles.selected : ""}`}
            ref={clickRef}
        >
            <AreaHeader
                currentAddress={currentAddress}
                isDisabled={isDisabled}
                isFetching={isFetching}
                isOpen={isOpen}
                onRemove={handleRemove}
                onToggle={() => setIsOpen(!isOpen)}
                onEdit={() => {
                    if (!isOpen || !showForm) {
                        setIsOpen(true);
                        setShowForm(true);
                    } else {
                        setShowForm(false);
                    }
                }}
            />

            {showForm && (
                <div className={styles.mvAreaFormWrapper}>
                    <AreaInputForm
                        addressId={addressData.id}
                        postalCode={postalCode}
                        streetName={streetName}
                        houseNumber={houseNumber}
                        isFetching={isFetching}
                        countryCode={countryCode}
                        isDisabled={isDisabled}
                        setHouseNumber={setHouseNumber}
                        setPostalCode={setPostalCode}
                        setStreetName={setStreetName}
                    />
                    {fetchResults.length > 0 && (
                        <div className={styles.mvFetchResultsContainer}>
                            <h3>
                                <Text
                                    id="general.labels.addressFound"
                                    plural={fetchResults.length}
                                >
                                    Addresses found
                                </Text>
                                :
                            </h3>
                            <ul className={styles.mvFetchResults}>
                                {fetchResults.map((result: MvAdressResult) => {
                                    return (
                                        <AreaFetchResult
                                            key={
                                                result.bag_adresseerbaarobjectid ||
                                                result.id
                                            }
                                            result={result}
                                            onClick={(res) => {
                                                onChange(addressData.id, res);
                                                setFetchResults([]);
                                                setShowForm(false);
                                                onSelect(addressData.id);
                                            }}
                                        />
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {error && <span className={styles.mvAreaError}>{error}</span>}

            <AreaFilters
                addressId={addressData.id}
                params={addressData.inputParams}
                isOpen={isOpen}
                isFetching={isFetching}
                countryCode={countryCode}
                defaultOptions={defaultOptions}
                onUpdateParams={onUpdateParams}
            />

            <div className={styles.mvAreaAddresses}>
                <span>
                    €{" "}
                    {Number(selectedAddressCount * pricePerAddress).toFixed(2)}
                </span>
                <Button
                    type="link"
                    disabled={selectedAddressCount === 0}
                    onClick={onShowAddresses}
                >
                    {selectedAddressCount}{" "}
                    <Text
                        id="general.labels.addresses"
                        plural={selectedAddressCount}
                    >{`adres${selectedAddressCount === 1 ? "" : "sen"}`}</Text>
                </Button>
            </div>
        </div>
    );
};
export default Area;
